import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { FIELD_TYPES, SxStyle } from '../../../types';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import firebaseApp from '../../../firebase-app';
import { useSnackbar } from 'notistack';
import Paper from '@mui/material/Paper';
import Chip, { ChipProps } from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import {
  query,
  limit,
  QueryDocumentSnapshot,
  orderBy,
  where,
  or,
  Query,
  DocumentData,
} from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';
import {
  MatchConfig,
  MatchDataGridFormat,
  MatchMinimized,
  convertFirestoreMatchToDatagridFormat,
} from './utils/data';
import { APP_TYPE } from '../../../types/app-config';
import { getMatchesConfigPath, getMatchesMinimizedPath } from '../../../utils/paths';
import { useHistory, useRouteMatch } from 'react-router-dom';
import EditMatchConfigModal from './components/edit-match-config-modal';
import SelectField from '../../common/fields/select-field';
import { useAppConfigs } from '../../../hooks/appConfigs';
import ListEditButton from '../../common/list-edit-button';
import SceneHeader from '../../common/scene/scene-header';
import ScenePage from '../../common/scene/scene-page';
import MatchPushNotificationColumn from './components/matches-push-notification-column';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 90px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  }),
};

interface ListStatsInfoProps {
  matchRow: MatchDataGridFormat;
}

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;
const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);
const SEASONS = Array.from(Array(1).keys())
  .map((i) => (CURRENT_YEAR - i).toString())
  .reverse();

const ListMatchConfig = ({ matchRow }: ListStatsInfoProps) => {
  const { strings } = useStringsContext();
  const { hideStats, showInterruptedMatchInformation, skipPush, hideMatchTime } =
    matchRow.matchConfig || {};

  const chips: { label: string; color?: ChipProps['color'] }[] = [
    {
      value: hideStats?.ballPossesion,
      label: strings.matches.hideBallPossesion,
    },
    { value: hideStats?.distance, label: strings.matches.hideDistance },
    {
      value: showInterruptedMatchInformation,
      label: strings.matches.showInterruptedMatchInformation,
    },
    { value: skipPush, label: strings.matches.pauseNotifications },
    { value: hideMatchTime, label: strings.matches.hideMatchTime },
  ]
    .filter((chip) => chip.value)
    .map((chip) => ({ label: chip.label, color: 'warning' }));

  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {chips.map((chip) => (
        <Chip key={chip.label} label={chip.label} size="small" color={chip.color} />
      ))}
    </Stack>
  );
};

export default function MatchList() {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { params } = useRouteMatch<{ abbrv?: string }>();
  const selectedAbbrv = params.abbrv || null;
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [matchDocs, setMatchDocs] = useState<QueryDocumentSnapshot[] | null>(null);
  const [matchConfigsDocs, setMatchConfigsDocs] = useState<QueryDocumentSnapshot[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchDataGridFormat | null>(null);
  const { appConfigs } = useAppConfigs();

  const appConfig = appConfigs?.find((config: any) => config.appShortname === selectedAbbrv);

  const onEdit = (match: MatchDataGridFormat) => {
    setSelectedMatch(match);
    setShowEditModal(true);
  };

  const columns = useMemo(
    (): GridColDef[] => [
      { field: 'startDate', headerName: strings.matches.startDate, width: 150 },
      { field: 'round', headerName: strings.matches.round },
      { field: 'homeTeamName', headerName: strings.matches.homeTeam, flex: 1 },
      {
        field: 'visitingTeamName',
        headerName: strings.matches.visitingTeam,
        flex: 1,
      },
      { field: 'status', headerName: strings.matches.status },
      { field: 'result', headerName: strings.matches.result, align: 'center' },
      {
        field: 'matchConfig',
        headerName: strings.matches.matchSettings,
        renderCell: (params: GridRenderCellParams) => <ListMatchConfig matchRow={params.row} />,
        flex: 1,
      },

      {
        field: 'push-notification',
        headerName: strings.matches.pushNotificationColumn,
        width: 300,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>
            {selectedAbbrv && appConfig?.configLeagueName && selectedSeason ? (
              <MatchPushNotificationColumn
                match={params.row}
                selectedAbbrv={selectedAbbrv}
                leagueAbbrv={appConfig.configLeagueName}
                season={selectedSeason}
              />
            ) : null}
          </>
        ),
        align: 'center',
      },
      {
        field: 'edit',
        headerName: '',
        minWidth: 60,
        maxWidth: 60,
        align: 'right',
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ListEditButton onEdit={() => onEdit(params.row)} />
        ),
      },
    ],
    [strings, appConfig, selectedAbbrv, selectedSeason]
  );

  useEffect(() => {
    if (!selectedSeason && appConfig?.currentSeason) {
      setSelectedSeason(appConfig.currentSeason.toString());
    }
  }, [appConfig, selectedSeason]);

  const getMatchList = useCallback(() => {
    if (appConfig?.configLeagueName && selectedSeason) {
      const getMatchDocs = async () => {
        setLoading(true);

        try {
          const path = getMatchesMinimizedPath(appConfig.configLeagueName, selectedSeason);
          const colRef = collection(firebaseApp.db, path);
          let matchQuery: Query<DocumentData>;

          if (appConfig.appType === APP_TYPE.CLUB) {
            matchQuery = query(
              colRef,
              or(
                where('homeTeamAbbrv', '==', appConfig.appShortname),
                where('visitingTeamAbbrv', '==', appConfig.appShortname)
              ),
              orderBy('startDate', 'desc'),
              limit(50)
            );
          } else {
            matchQuery = query(colRef, orderBy('startDate', 'desc'), limit(50));
          }

          const { docs: matchDocs } = await getDocs(matchQuery);

          const matchesConfigPath = getMatchesConfigPath(
            appConfig.configLeagueName,
            selectedSeason
          );
          const matchesConfigQuery = query(collection(firebaseApp.db, matchesConfigPath));
          const { docs: matchConfigs } = await getDocs(matchesConfigQuery);

          setMatchDocs(matchDocs);
          setMatchConfigsDocs(matchConfigs);
        } catch (error) {
          enqueueSnackbar(strings.messages.saveError, {
            variant: 'error',
          });
        }

        setLoading(false);
      };
      getMatchDocs();
    }
  }, [appConfig, enqueueSnackbar, strings, selectedSeason]);

  useEffect(() => {
    getMatchList();
  }, [selectedSeason, getMatchList]);

  const rowsSent = useMemo(() => {
    const matches = matchDocs?.map((doc) => doc.data()) as MatchMinimized[];
    const matchConfigs = matchConfigsDocs?.map((doc) => doc.data()) as MatchConfig[];
    return matches?.map((match) => convertFirestoreMatchToDatagridFormat(match, matchConfigs));
  }, [matchConfigsDocs, matchDocs]);

  const onRowClick = (params: GridRowParams): void => {
    if (appConfig) {
      history.push(`/matches/${appConfig.configLeagueName}/${selectedSeason}/${params.row.id}`);
    }
  };

  return (
    <ScenePage title={strings.appBar.matches}>
      <SceneHeader title={strings.appBar.matches}>
        <SelectField
          fullWidth
          size="small"
          setChange={(key, value) => setSelectedSeason(value)}
          setValidation={null}
          field={{
            title: strings.matches.season,
            id: 'season',
            type: FIELD_TYPES.SELECT,
            key: 'season',
            value: selectedSeason,
            options: SEASONS,
          }}
        />
      </SceneHeader>
      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rows={rowsSent || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          onRowClick={onRowClick}
          loading={loading}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'startDate',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Paper>
      {showEditModal && selectedMatch && appConfig && selectedSeason && (
        <EditMatchConfigModal
          onDone={getMatchList}
          matchId={selectedMatch.id}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          leagueAbbrv={appConfig.configLeagueName}
          season={selectedSeason}
        />
      )}
    </ScenePage>
  );
}
